import { FC, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ProductMaster,
  ProductMasterType,
  WorkshopProductInput,
  WorkshopProductVariantInput,
  createWorkshopProduct,
  fetchProductMaster,
  updateProductMasterActive,
  updateWorkshopProduct,
} from '../../api/productMasters';
import { fetchProductBrands, ProductBrand } from '../../api/productBrands';
import { fetchProductTemplates, ProductTemplate } from '../../api/productTemplates';
import { fetchProductCategories, ProductCategory } from '../../api/products';
import AddVariantModal, { VariantAttributeDefinition, VariantDraft } from './components/AddVariantModal';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';
import { removeProductVariant, updateProductVariantActive } from '../../api/productVariants';

interface MastersCreatePanelProps {
  onCancel?: () => void;
  servicesOnly?: boolean;
  masterId?: string | null;
}

interface ProductFormState {
  name: string;
  description: string;
  code: string;
  brandErpId: string;
  variantTemplateErpId: string;
  categoryIds: string[];
  articleCore: string;
  type: ProductMasterType;
  isActive: boolean;
}

const createInitialForm = (servicesOnly: boolean): ProductFormState => ({
  name: '',
  description: '',
  code: '',
  brandErpId: '',
  variantTemplateErpId: '',
  categoryIds: [],
  articleCore: '',
  type: servicesOnly ? 'service' : 'regular',
  isActive: true,
});

const extractArticleCore = (articleNumber: string): string =>
  articleNumber.replace(/^SM\s*-\s*/i, '').trim();

const generateLocalId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const MastersCreatePanel: FC<MastersCreatePanelProps> = ({ onCancel, servicesOnly = false, masterId }) => {
  const navigate = useNavigate();
  const isEditing = Boolean(masterId);
  const erpAdminBaseUrl = useMemo(
    () => (process.env.REACT_APP_ERP_ADMIN_URL ?? '').replace(/\/$/, ''),
    [],
  );
  const draftProductsUrl = erpAdminBaseUrl
    ? `${erpAdminBaseUrl}/admin/draft-products`
    : '/admin/draft-products';
  const [form, setForm] = useState<ProductFormState>(() => createInitialForm(servicesOnly));
  const [variants, setVariants] = useState<VariantDraft[]>([]);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [brandOptions, setBrandOptions] = useState<ProductBrand[]>([]);
  const [templateOptions, setTemplateOptions] = useState<ProductTemplate[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<ProductCategory[]>([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement | null>(null);
  const categoryToggleBtnRef = useRef<HTMLButtonElement | null>(null);
  const [existingMaster, setExistingMaster] = useState<ProductMaster | null>(null);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [masterError, setMasterError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [brands, templates, categories] = await Promise.all([
          fetchProductBrands({ page: 1, limit: 100 }),
          fetchProductTemplates({ page: 1, limit: 100 }),
          fetchProductCategories({ page: 1, limit: 100 }),
        ]);

        if (!isMounted) {
          return;
        }

        setBrandOptions(brands.data);
        setTemplateOptions(templates.data);
        setCategoryOptions(categories.data);
      } catch (err) {
        console.error('Failed to load workshop product helpers', err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!categoryDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!categoryDropdownRef.current) {
        return;
      }

      if (!categoryDropdownRef.current.contains(event.target as Node)) {
        setCategoryDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [categoryDropdownOpen]);

  useEffect(() => {
    if (!masterId) {
      setExistingMaster(null);
      setMasterError(null);
      setLoadingMaster(false);
      setForm(createInitialForm(servicesOnly));
      setVariants([]);
      return;
    }

    setLoadingMaster(true);
    setMasterError(null);

    fetchProductMaster(masterId)
      .then((master) => {
        if (!master) {
          throw new Error('Service not found.');
        }
        setExistingMaster(master);
        // Derive selected category ERP IDs from API response
        const selectedCategoryErpIds: string[] = (() => {
          const fromIds = Array.isArray((master as any).categoryIds)
            ? (master as any).categoryIds
            : Array.isArray((master as any).category_ids)
              ? (master as any).category_ids
              : null;

          if (Array.isArray(fromIds)) {
            return fromIds
              .map((id: any) => Number(id))
              .filter((n: number) => Number.isFinite(n) && n > 0)
              .map((n: number) => String(n));
          }

          const cats = Array.isArray((master as any).categories)
            ? (master as any).categories
            : [];
          return cats
            .map((c: any) => Number(c?.erpId ?? c?.erp_id ?? c?.id))
            .filter((n: number) => Number.isFinite(n) && n > 0)
            .map((n: number) => String(n));
        })();

        setForm({
          name: master.name ?? '',
          description: (master as any).description ?? '',
          code: '',
          brandErpId: master.brand?.erpId ? String(master.brand.erpId) : '',
          variantTemplateErpId: master.variantTemplate?.erpId ? String(master.variantTemplate.erpId) : '',
          categoryIds: selectedCategoryErpIds,
          articleCore: extractArticleCore(master.articleNumber ?? ''),
          type: (master.type as ProductMasterType) ?? (servicesOnly ? 'service' : 'regular'),
          // Master products are always treated as active for workshop flows.
          isActive: true,
        });
        setVariants(
          (master.variants ?? []).map((variant) => ({
            id: variant.id ?? `erp-${variant.erpId ?? generateLocalId()}`,
            erpId: variant.erpId,
            name: variant.name ?? '',
            // Prefer ERP-provided variant code; do not fall back to article number
            // to avoid sending duplicate codes across variants.
            code: (variant as any).variantCode ?? '',
            description: (variant as any).description ?? '',
            upc: variant.upc ?? '',
            retailPrice: variant.retailPrice ? String(variant.retailPrice) : '',
            staffPrice: '',
            isTemporarilyUnavailable: !(variant.isVariantEnabled ?? true),
            isAvailableInPos: true,
            isAvailableInEcommerce: true,
            isSoldAsSingleItem: true,
            isActive: (variant as any).isActive ?? true,
            productVariantValues: Array.isArray((variant as any).values)
              ? (variant as any).values
                .map((item: any) => ({
                  id: Number(
                    item.attributeErpId ??
                    item.attribute_erp_id ??
                    item.attribute?.erpId,
                  ),
                  selectedValue: String(item.value ?? ''),
                  attributeName: item.attribute?.name,
                }))
                .filter(
                  (item: {
                    id: number;
                    selectedValue: string;
                    attributeName?: string;
                  }) => Number.isFinite(item.id) && item.id > 0,
                )
              : [],
          })),
        );
      })
      .catch((err) => {
        setExistingMaster(null);
        setMasterError(err instanceof Error ? err.message : 'Failed to load service details.');
      })
      .finally(() => setLoadingMaster(false));
  }, [masterId, servicesOnly]);

  const parseOptionalNumber = (value: string): number | undefined => {
    const trimmed = value.trim();

    if (!trimmed) {
      return undefined;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const navigateBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(servicesOnly ? '/inventory/services' : '/inventory/masters');
    }
  };

  const handleInputChange = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = event.currentTarget;
    const { name, value } = target;

    if ('type' in target && target.type === 'checkbox') {
      const checkbox = target as HTMLInputElement;
      setForm((prev) => ({
        ...prev,
        [name]: checkbox.checked,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (event: FormEvent<HTMLSelectElement>) => {
    const target = event.currentTarget;
    const { name, value, multiple, selectedOptions } = target;

    if (multiple) {
      const values = Array.from(selectedOptions).map((option) => option.value);
      setForm((prev) => ({
        ...prev,
        categoryIds: values,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVariantRemoveLocal = (id: string) => {
    setVariants((prev) => prev.filter((item) => item.id !== id));
  };

  const [variantEditingDraft, setVariantEditingDraft] = useState<VariantDraft | null>(null);

  const parsedCategoryIds = useMemo(() => {
    return form.categoryIds
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0);
  }, [form.categoryIds]);

  const selectedTemplateAttributes: VariantAttributeDefinition[] | null = useMemo(() => {
    if (!form.variantTemplateErpId) {
      return null;
    }
    const template = templateOptions.find(
      (item) => String(item.erpId) === String(form.variantTemplateErpId),
    );
    if (!template || !Array.isArray(template.attributes)) {
      return null;
    }
    return template.attributes.map((attribute) => ({
      id: attribute.id,
      name: attribute.name,
      erpId: attribute.erpId,
      defaultValue: attribute.defaultValue,
      options: Array.isArray(attribute.options) ? attribute.options : [],
    }));
  }, [form.variantTemplateErpId, templateOptions]);

  const buildPayload = (): WorkshopProductInput => {
    const articleCore = form.articleCore.replace(/^SM\s*-\s*/i, '').trim();

    if (!articleCore) {
      throw new Error('Article number is required.');
    }

    if (!form.name.trim()) {
      throw new Error('Product name is required.');
    }

    if (variants.length === 0) {
      throw new Error('Add at least one variant.');
    }

    const variantInputs: WorkshopProductVariantInput[] = variants.map((variant) => {
      const isTemporarilyUnavailable =
        typeof variant.isActive === 'boolean'
          ? !variant.isActive
          : variant.isTemporarilyUnavailable;

      const base: WorkshopProductVariantInput = {
        name: variant.name.trim(),
        code: variant.code.trim() || undefined,
        description: variant.description.trim() || undefined,
        upc: variant.upc.trim() || undefined,
        retailPrice: variant.retailPrice.trim() || undefined,
        staffPrice: variant.staffPrice.trim() || undefined,
        isTemporarilyUnavailable,
        isAvailableInPos: variant.isAvailableInPos,
        isAvailableInEcommerce: variant.isAvailableInEcommerce,
        isSoldAsSingleItem: variant.isSoldAsSingleItem,
      };

      if (Array.isArray(variant.productVariantValues) && variant.productVariantValues.length > 0) {
        base.productVariantValues = variant.productVariantValues
          .map((item) => ({
            id: Number(item.id),
            selectedValue: String(item.selectedValue ?? '').trim(),
          }))
          .filter(
            (item) =>
              Number.isFinite(item.id) &&
              item.id > 0 &&
              item.selectedValue.length > 0,
          );
      }

      if (variant.erpId && Number.isFinite(variant.erpId)) {
        base.erpId = Number(variant.erpId);
      }

      return base;
    });

    const originalCreatedAt = existingMaster?.originalCreatedAt ?? new Date().toISOString();

    const payload: WorkshopProductInput = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      code: form.code.trim() || undefined,
      brandId: parseOptionalNumber(form.brandErpId),
      variantTemplateId: parseOptionalNumber(form.variantTemplateErpId),
      categoryIds: parsedCategoryIds.length ? parsedCategoryIds : undefined,
      articleNumber: `SM - ${articleCore}`,
      externalMasterProductId: articleCore,
      isWarranty: false,
      hasBatch: false,
      isNonInventory: false,
      type: servicesOnly ? 'service' : form.type,
      originalCreatedAt,
      variants: variantInputs,
    };

    if (existingMaster) {
      payload.erpId = existingMaster.erpId;
    }

    return payload;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = buildPayload();

      if (existingMaster) {
        const updated = await updateWorkshopProduct(existingMaster.erpId, payload);
        // Sync active flags for variants locally based on current draft
        if (Array.isArray(updated.variants) && updated.variants.length) {
          const byErpId = new Map<number, VariantDraft>();
          variants.forEach((draft) => {
            if (draft.erpId && Number.isFinite(draft.erpId)) {
              byErpId.set(Number(draft.erpId), draft);
            }
          });
          // best-effort updates; ignore errors
          await Promise.all(
            updated.variants.map(async (variant: any) => {
              const draft = byErpId.get(variant.erpId);
              if (!draft || typeof draft.isActive === 'undefined') return;
              const current = typeof variant.isActive === 'boolean' ? variant.isActive : true;
              if (current === draft.isActive) return;
              try {
                await updateProductVariantActive(variant.id, draft.isActive);
              } catch {
                // ignore per-variant failure
              }
            }),
          );
        }
        setSuccessMessage('Service updated. Visit the ERP admin panel to approve changes.');
        setExistingMaster(updated);
        setForm((prev) => ({
          ...prev,
          name: payload.name ?? prev.name,
          description: payload.description ?? '',
          code: payload.code ?? '',
          brandErpId: payload.brandId ? String(payload.brandId) : '',
          variantTemplateErpId: payload.variantTemplateId ? String(payload.variantTemplateId) : '',
          categoryIds: payload.categoryIds ? payload.categoryIds.map((item) => String(item)) : [],
          articleCore: extractArticleCore(payload.articleNumber ?? ''),
          type: payload.type ?? prev.type,
          isActive: form.isActive,
        }));
        if (updated.variants) {
          const byErpId = new Map<number, VariantDraft>();
          variants.forEach((draft) => {
            if (draft.erpId && Number.isFinite(draft.erpId)) {
              byErpId.set(Number(draft.erpId), draft);
            }
          });
          setVariants(
            updated.variants.map((variant) => {
              const draft = byErpId.get(variant.erpId);
              return {
                id: variant.id ?? generateLocalId(),
                erpId: variant.erpId,
                name: variant.name ?? '',
                // Prefer ERP-provided variant code; otherwise keep the draft's code
                code: (variant as any).variantCode ?? draft?.code ?? '',
                description: (variant as any).description ?? draft?.description ?? '',
                upc: variant.upc ?? '',
                retailPrice: variant.retailPrice ? String(variant.retailPrice) : '',
                staffPrice: '',
                // Prefer DB value if present, otherwise fall back to the draft's isActive value
                isActive:
                  typeof (variant as any).isActive === 'boolean'
                    ? (variant as any).isActive
                    : typeof draft?.isActive === 'boolean'
                      ? draft.isActive
                      : true,
                isTemporarilyUnavailable: !(variant.isVariantEnabled ?? true),
                isAvailableInPos: true,
                isAvailableInEcommerce: true,
                isSoldAsSingleItem: true,
                productVariantValues: Array.isArray((variant as any).values)
                  ? (variant as any).values
                    .map((item: any) => ({
                      id: Number(
                        item.attributeErpId ??
                        item.attribute_erp_id ??
                        item.attribute?.erpId,
                      ),
                      selectedValue: String(item.value ?? ''),
                      attributeName: item.attribute?.name,
                    }))
                    .filter(
                      (item: {
                        id: number;
                        selectedValue: string;
                        attributeName?: string;
                      }) => Number.isFinite(item.id) && item.id > 0,
                    )
                  : [],
              };
            }),
          );
        }
      } else {
        await createWorkshopProduct(payload);
        setSuccessMessage(
          servicesOnly
            ? 'Draft service created. Visit the ERP admin panel to approve this service.'
            : 'Draft product created. Visit the ERP admin panel to approve this product.',
        );
        setForm(createInitialForm(servicesOnly));
        setVariants([]);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create product.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-5 p-5">
      <PageHeaderWithBack
        title={masterId ? 'Edit Service' : servicesOnly ? 'Create Service' : 'Create Product'}
        description={servicesOnly
          ? 'Fill in the service details. Article numbers are automatically prefixed with SM -.'
          : 'Fill in the product details. Article numbers are automatically prefixed with SM -.'}
        onBack={navigateBack}
        backLabel={servicesOnly ? 'Back to services' : 'Back to master products'}
      />

      {isEditing ? (
        loadingMaster ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Loading service details…
          </div>
        ) : masterError ? (
          <div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
            {masterError}
            <div className="mt-3">
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary/90"
                onClick={navigateBack}
              >
                Back to services
              </button>
            </div>
          </div>
        ) : null
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-success/40 bg-success/10 px-4 py-3 text-sm text-success">
          {successMessage}{' '}
          <a
            href={draftProductsUrl}
            className="font-semibold underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to admin panel
          </a>
        </div>
      ) : null}
      {(!isEditing || (!loadingMaster && !masterError)) ? (
        <>
          <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500 md:col-span-2">
                Name *
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                />
              </label>

              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                Code
                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleInputChange}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </label>

              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                Brand
                <select
                  name="brandErpId"
                  value={form.brandErpId}
                  onChange={handleSelectChange}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Select brand</option>
                  {brandOptions.map((brand) => (
                    <option key={brand.id} value={String(brand.erpId)}>
                      {brand.name} (ERP {brand.erpId})
                    </option>
                  ))}
                </select>
              </label>

              {/* Master products are always treated as active for workshop flows,
              so we no longer show a toggle for master status here. */}

              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                Variant Template *
                <select
                  name="variantTemplateErpId"
                  value={form.variantTemplateErpId}
                  onChange={handleSelectChange}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">Select template</option>
                  {templateOptions.map((template) => (
                    <option key={template.id} value={String(template.erpId)}>
                      {template.name} (ERP {template.erpId})
                    </option>
                  ))}
                </select>
                <span className="text-[11px] font-normal text-slate-400">
                  Select a variant template to enable the “+ Add Variant” button.
                </span>
              </label>

              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500 md:col-span-2">
                Description
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </label>

              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500 md:col-span-2">
                Categories
                <div className="relative" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    onClick={() => setCategoryDropdownOpen((prev) => !prev)}
                    ref={categoryToggleBtnRef}
                  >
                    <span>
                      {form.categoryIds.length
                        ? `${form.categoryIds.length} categor${form.categoryIds.length === 1 ? 'y' : 'ies'} selected`
                        : 'Select categories'}
                    </span>
                    <span className="text-xs text-slate-400">▾</span>
                  </button>

                  {categoryDropdownOpen && (
                    <div className="absolute z-30 mt-2 max-h-64 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-primary/10">
                      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 text-xs text-slate-500">
                        <span>Select Categories</span>
                        <button
                          type="button"
                          className="rounded-md border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:border-primary hover:text-primary"
                          onMouseDown={(e) => {
                            // Prevent bubbling to parent containers
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCategoryDropdownOpen(false);
                            // Blur the toggle button to avoid accidental reopen via focus/keypress
                            categoryToggleBtnRef.current?.blur();
                          }}
                        >
                          Done
                        </button>
                      </div>
                      <div className="max-h-52 overflow-y-auto p-3 space-y-2 text-sm">
                        {categoryOptions.length === 0 ? (
                          <p className="text-xs text-slate-400">No categories loaded.</p>
                        ) : (
                          categoryOptions.map((category) => {
                            const value = String(category.erpId);
                            const checked = form.categoryIds.includes(value);

                            return (
                              <label
                                key={category.id}
                                className="flex items-center gap-2 rounded-lg px-2 py-1 text-slate-600 hover:bg-primary/5"
                              >
                                <input
                                  type="checkbox"
                                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                                  checked={checked}
                                  onChange={() => {
                                    setForm((prev) => ({
                                      ...prev,
                                      categoryIds: checked
                                        ? prev.categoryIds.filter((item) => item !== value)
                                        : [...prev.categoryIds, value],
                                    }));
                                  }}
                                />
                                <span className="flex-1 text-sm">
                                  {category.name}
                                  <span className="ml-2 text-xs text-slate-400">ERP {category.erpId}</span>
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <span className="text-[11px] text-slate-400">
                  {form.categoryIds.length
                    ? (() => {
                      const names = categoryOptions
                        .filter((c) => form.categoryIds.includes(String(c.erpId)))
                        .map((c) => c.name)
                        .filter(Boolean);
                      return names.length ? names.join(', ') : 'No categories selected';
                    })()
                    : 'No categories selected'}
                </span>
              </label>

              <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
                Article Number Suffix *
                <div className="flex items-center gap-2">
                  <span className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-2 text-xs text-slate-500">
                    SM-
                  </span>
                  <input
                    type="text"
                    name="articleCore"
                    value={form.articleCore}
                    onChange={handleInputChange}
                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Enter article number suffix"
                    required
                  />
                </div>
              </label>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">

              <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-slate-700">Variants</h3>
                  <p className="text-xs text-slate-500">Add one or more variants to sync with the ERP.</p>
                </div>

                <button
                  type="button"
                  className="ml-auto rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-500 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={!form.variantTemplateErpId}
                  onClick={() => {
                    if (!form.variantTemplateErpId) return;
                    setVariantEditingDraft(null);
                    setVariantModalOpen(true);
                  }}
                >
                  + Add Variant
                </button>
              </div>

              {variants.length === 0 ? (
                <p className="text-sm text-slate-500">No variants added yet.</p>
              ) : (
                <ul className="space-y-3">
                  {variants.map((variant) => (
                    <li
                      key={variant.id}
                      className="flex items-start rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-600"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="font-semibold text-slate-700">{variant.name}</div>
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                          >
                            <span
                              className={
                                (variant.isActive ?? true)
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-slate-200 bg-slate-100 text-slate-500'
                              }
                            >
                              {(variant.isActive ?? true) ? 'Active' : 'Inactive'}
                            </span>
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-slate-400 flex gap-4">
                          <span>Variant Code: {variant.code || '—'}</span>
                          <span>UPC: {variant.upc || '—'}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Retail Price: {variant.retailPrice ? variant.retailPrice : '—'}</span>
                          <span>Staff Price: {variant.staffPrice ? variant.staffPrice : '—'}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          {variant.description ? variant.description : 'No description'}
                        </div>
                        {Array.isArray(variant.productVariantValues) && variant.productVariantValues.length > 0 && (
                          <div className="mt-2 text-xs text-slate-500">
                            <div className="font-semibold text-slate-600 mb-1">Attributes</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {variant.productVariantValues.map((attr, index) => (
                                <span key={`${attr.id}-${index}`} className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-600 border border-slate-200">
                                  <span className="font-semibold mr-1">
                                    {(attr as any).attributeName ?? String(attr.id)}:
                                  </span>
                                  <span>{attr.selectedValue || '—'}</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="ml-auto flex gap-2">
                        <button
                          type="button"
                          className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
                          onClick={() => {
                            setVariantEditingDraft(variant);
                            setVariantModalOpen(true);
                          }}
                        >
                          Edit
                        </button>
                        {variant.erpId ? (
                          <button
                            type="button"
                            className="rounded-lg border border-danger/30 px-3 py-1 text-xs font-medium text-danger hover:bg-danger/10"
                            onClick={async () => {
                              const confirmed = window.confirm(
                                'Are you sure you want to remove this service variant? This will also remove it from the ERP.',
                              );
                              if (!confirmed) return;

                              try {
                                await removeProductVariant(variant.id);
                                if (existingMaster) {
                                  const refreshed = await fetchProductMaster(existingMaster.id);
                                  setExistingMaster(refreshed);
                                  setVariants(
                                    (refreshed.variants ?? []).map((v) => ({
                                      id: v.id ?? generateLocalId(),
                                      erpId: v.erpId,
                                      name: v.name ?? '',
                                      code: (v as any).variantCode ?? v.articleNumber ?? '',
                                      description: (v as any).description ?? '',
                                      upc: v.upc ?? '',
                                      retailPrice: v.retailPrice ? String(v.retailPrice) : '',
                                      staffPrice: '',
                                      isTemporarilyUnavailable: !(v.isVariantEnabled ?? true),
                                      isAvailableInPos: true,
                                      isAvailableInEcommerce: true,
                                      isSoldAsSingleItem: true,
                                      isActive: (v as any).isActive ?? true,
                                      productVariantValues: Array.isArray((v as any).values)
                                        ? (v as any).values
                                          .map((item: any) => ({
                                            id: Number(
                                              item.attributeErpId ??
                                              item.attribute_erp_id ??
                                              item.attribute?.erpId,
                                            ),
                                            selectedValue: String(item.value ?? ''),
                                            attributeName: item.attribute?.name,
                                          }))
                                          .filter(
                                            (item: {
                                              id: number;
                                              selectedValue: string;
                                              attributeName?: string;
                                            }) => Number.isFinite(item.id) && item.id > 0,
                                          )
                                        : [],
                                    })),
                                  );
                                } else {
                                  handleVariantRemoveLocal(variant.id);
                                }
                              } catch (err) {
                                const message =
                                  err instanceof Error
                                    ? err.message
                                    : 'Failed to remove service variant.';
                                setError(message);
                              }
                            }}
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="rounded-lg border border-danger/30 px-3 py-1 text-xs font-medium text-danger hover:bg-danger/10"
                            onClick={() => handleVariantRemoveLocal(variant.id)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-500 hover:text-primary"
                onClick={navigateBack}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary/90 disabled:opacity-60"
                disabled={submitting}
              >
                {submitting
                  ? 'Saving...'
                  : isEditing
                    ? 'Save changes'
                    : servicesOnly
                      ? 'Create Service'
                      : 'Create Product'}
              </button>
            </div>
          </form>

          <AddVariantModal
            open={variantModalOpen}
            onClose={() => {
              setVariantModalOpen(false);
              setVariantEditingDraft(null);
            }}
            onSave={(variant) => {
              setVariants((prev) => {
                const exists = prev.some((item) => item.id === variant.id);
                if (exists) {
                  return prev.map((item) => (item.id === variant.id ? variant : item));
                }
                return [...prev, variant];
              });
              setVariantEditingDraft(null);
            }}
            initialVariant={variantEditingDraft}
            templateAttributes={selectedTemplateAttributes}
            masterName={form.name}
          />
        </>
      ) : null}
    </div>
  );
};

export default MastersCreatePanel;
